import { Check } from "lucide-react";

const STEPS = [
  { n: 1, name: "A/D bias", time: "09:20" },
  { n: 2, name: "Sector", time: "09:35" },
  { n: 3, name: "Top stocks", time: "09:35" },
  { n: 4, name: "Range break", time: "09:20–30" },
  { n: 5, name: "Lowest vol", time: "all stocks · 09:35+" },
  { n: 6, name: "Best reversal", time: "1 trade at a time" },
  { n: 7, name: "SL order", time: "–14:00" },
  { n: 8, name: "Exit", time: "2R · trail · 15:15" },
];

export function stepsDone(st) {
  if (!st) return 0;
  const kinds = new Set((st.events || []).map((e) => e.kind));
  let n = 0;
  if (st.bias) n = 1;
  if (st.sector) n = 2;
  if (st.stocks?.length) n = 3;
  if (kinds.has("break")) n = 4;
  if (kinds.has("lowvol") && kinds.has("break")) n = 5;
  if (kinds.has("signal")) n = 6;
  if (kinds.has("fill") || kinds.has("triggered")) n = 7;
  if (kinds.has("exit")) n = 8;
  return n;
}

function Detail({ n, st }) {
  if (!st) return null;
  const long = st.bias === "LONG";
  if (n === 1 && st.bias)
    return <div className={`num text-[11px] font-semibold ${long ? "up" : st.bias === "SHORT" ? "down" : "text-slate-400"}`} data-testid="step-1-detail">{st.bias}{st.ad ? ` · ${st.ad.ratio}` : ""}</div>;
  if (n === 2 && st.sector) {
    const r = (st.ranking || []).find((x) => x.sector === st.sector);
    return <div className="text-[11px] text-cyan-200 font-semibold truncate" data-testid="step-2-detail" title={st.sector}>
      {st.sector}{r ? <span className={`num ml-1 ${r.avg >= 0 ? "up" : "down"}`}>{r.avg >= 0 ? "+" : ""}{r.avg}%</span> : null}</div>;
  }
  if (n === 3 && st.stocks?.length)
    return <div data-testid="step-3-detail">{st.stocks.map((s) => (
      <div key={s.sym} className="num text-[11px] flex justify-between gap-1"><span className="text-slate-100 truncate">{s.sym}</span>
        <span className={s.pct >= 0 ? "up" : "down"}>{s.pct >= 0 ? "+" : ""}{s.pct}%</span></div>))}</div>;
  if (n === 4 && st.stocks?.length)
    return <div data-testid="step-4-detail">{st.stocks.map((s) => (
      <div key={s.sym} className="num text-[11px] flex justify-between gap-1" data-testid={`break-${s.sym}`}>
        <span className="text-slate-300 truncate">{s.sym}</span>
        {s.broke ? <span className="text-cyan-300 flex items-center gap-0.5"><Check size={10} />broke</span>
          : <span className="text-amber-300">{long ? ">" : "<"}{long ? s.or_high : s.or_low}</span>}</div>))}</div>;
  if (n === 5 && st.low)
    return <div className="num text-[11px]" data-testid="step-5-detail">
      <div className="flex justify-between gap-1"><span className="text-slate-100 truncate">{st.low.sym}</span><span className="text-slate-400">{st.low.t}</span></div>
      <div className="text-cyan-200">{st.low.rv}x avg vol</div></div>;
  if (n === 6) {
    const o = st.positions?.[0] || st.tickets?.[0];
    if (!o) return null;
    return <div className="num text-[11px]" data-testid="step-6-detail">
      <div className={`font-semibold ${o.side === "LONG" ? "up" : "down"}`}>{o.sym}</div>
      <div className="text-slate-400">{st.positions?.[0] ? "in trade" : o.awaiting ? "await Y" : "order set"}{o.rv ? ` · ${o.rv}x` : ""}</div></div>;
  }
  return null;
}

export default function Pipeline({ state }) {
  const done = stepsDone(state);
  const noTrade = state?.bias === "NONE";
  return (
    <div className="panel p-3" data-testid="step-pipeline">
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-[0.9fr_1fr_1.35fr_1.35fr_1.1fr_1fr_0.8fr_0.9fr] gap-1.5 items-stretch">
        {STEPS.map((s) => {
          const ok = s.n <= done;
          const cur = s.n === done + 1 && state?.status === "running" && !noTrade;
          return (
            <div key={s.n} data-testid={`step-${s.n}`}
              className={`rounded-md px-2.5 py-2 border transition-colors ${ok ? "border-cyan-500/40 bg-cyan-500/5" : cur ? "border-amber-500/50 bg-amber-500/5" : "border-[#1E293B]"}`}>
              <div className="flex items-center justify-between">
                <span className={`num text-[11px] ${ok ? "text-cyan-300" : cur ? "text-amber-300" : "text-slate-500"}`}>0{s.n}</span>
                {ok && <Check size={12} className="text-cyan-300" />}
              </div>
              <div className="text-[13px] text-slate-100 font-medium leading-tight mt-0.5">{s.name}</div>
              <div className="num text-[10px] text-slate-500">{s.time}</div>
              <div className="mt-1"><Detail n={s.n} st={state} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
